import { X } from 'lucide-react';
import { forwardRef } from 'react';
import { getReadableForeground } from '@/shared/lib/contrast';
import { cn } from '@/shared/lib/utils';
import { TruncatedText } from './truncated-text';

/**
 * LabelBadge sizes
 */
export type LabelBadgeSize = 'sm' | 'md';

/**
 * Label data for the badge
 */
export interface LabelData {
  /** Label display name */
  readonly name: string;
  /** Color - can be a CSS variable name (e.g., 'primary') or HSL value */
  readonly color: string;
  /** Optional emoji to display */
  readonly emoji?: string;
}

/**
 * Props for LabelBadge component
 */
export interface LabelBadgeProps {
  /** Label data to display */
  readonly label: LabelData;
  /** Size of the badge */
  readonly size?: LabelBadgeSize;
  /** Click handler for the badge */
  readonly onClick?: () => void;
  /** Whether the badge can be removed */
  readonly removable?: boolean;
  /** Handler when remove button is clicked */
  readonly onRemove?: () => void;
  /** Additional CSS classes */
  readonly className?: string;
}

/**
 * Size configurations
 */
const SIZE_CLASSES: Readonly<Record<LabelBadgeSize, string>> = {
  sm: 'gap-1 px-1.5 py-0.5 text-xs',
  md: 'gap-1.5 px-2 py-1 text-sm',
} as const;

/**
 * Predefined color mappings to CSS variables
 * These map to the theme's semantic colors
 */
const SEMANTIC_COLORS: Readonly<Record<string, string>> = {
  primary: 'hsl(var(--primary))',
  secondary: 'hsl(var(--secondary))',
  destructive: 'hsl(var(--destructive))',
  success: 'hsl(var(--success))',
  warning: 'hsl(var(--warning))',
  info: 'hsl(var(--info))',
  muted: 'hsl(var(--muted))',
} as const;

/**
 * Paired foreground tokens for each semantic color.
 * These auto-adapt per theme so background + text always have proper contrast.
 */
const SEMANTIC_FOREGROUNDS: Readonly<Record<string, string>> = {
  primary: 'hsl(var(--primary-foreground))',
  secondary: 'hsl(var(--secondary-foreground))',
  destructive: 'hsl(var(--destructive-foreground))',
  success: 'hsl(var(--success-foreground))',
  warning: 'hsl(var(--warning-foreground))',
  info: 'hsl(var(--info-foreground))',
  muted: 'hsl(var(--muted-foreground))',
} as const;

/**
 * Get color style (background + paired foreground).
 * For semantic color names the matching `*-foreground` token is applied inline so
 * text contrast adapts to every theme. Supports raw HSL or direct CSS values too.
 */
function getColorStyle(color: string): React.CSSProperties {
  // Check if it's a semantic color name
  const semanticColor = SEMANTIC_COLORS[color];
  if (semanticColor) {
    return {
      backgroundColor: semanticColor,
      color: SEMANTIC_FOREGROUNDS[color],
    };
  }

  // Check if it's an HSL value (e.g., "200 70% 50%")
  if (/^\d+\s+\d+%?\s+\d+%?$/.test(color)) {
    return { backgroundColor: `hsl(${color})` };
  }

  // Hex colour (e.g. "#3b82f6") — pair with an auto-contrast foreground so
  // custom configurable colours stay legible.
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color.trim())) {
    return {
      backgroundColor: color,
      color: getReadableForeground(color),
    };
  }

  // Otherwise treat as direct CSS color value
  return { backgroundColor: color };
}

/**
 * Fallback text color class for non-semantic (raw HSL / custom) colors.
 * Semantic colors derive their text color inline via the paired foreground token.
 */
function getTextColorClass(color: string): string {
  // Semantic colors get their text color from the paired foreground (inline).
  if (color in SEMANTIC_COLORS) {
    return '';
  }

  // Hex colours get an inline auto-contrast foreground (set in getColorStyle).
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color.trim())) {
    return '';
  }

  // Default to light text for custom colors.
  return 'text-white';
}

/**
 * LabelBadge - Colored label with optional emoji
 *
 * Reusable component for displaying event labels, task tags,
 * or album categories with consistent styling.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LabelBadge label={{ name: 'Важно', color: 'destructive', emoji: '⚠️' }} />
 *
 * // With remove button
 * <LabelBadge
 *   label={{ name: 'Работа', color: 'primary' }}
 *   removable
 *   onRemove={() => handleRemove('work')}
 * />
 *
 * // Custom HSL color
 * <LabelBadge label={{ name: 'Custom', color: '200 70% 50%' }} />
 * ```
 */
export const LabelBadge = forwardRef<HTMLSpanElement, LabelBadgeProps>(
  (
    { label, size = 'md', onClick, removable = false, onRemove, className },
    ref
  ) => {
    const colorStyle = getColorStyle(label.color);
    const textColorClass = getTextColorClass(label.color);
    const isInteractive = !!onClick;

    const handleRemove = (event: React.MouseEvent) => {
      event.stopPropagation();
      onRemove?.();
    };

    return (
      <span
        ref={ref}
        role={isInteractive ? 'button' : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tabIndex only applied when isInteractive
        tabIndex={isInteractive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          isInteractive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        style={colorStyle}
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          SIZE_CLASSES[size],
          textColorClass,
          isInteractive &&
            'cursor-pointer hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          className
        )}
      >
        {label.emoji && (
          <span className="shrink-0" aria-hidden="true">
            {label.emoji}
          </span>
        )}
        <TruncatedText>{label.name}</TruncatedText>
        {removable && onRemove && (
          <button
            type="button"
            onClick={handleRemove}
            className={cn(
              'ml-0.5 rounded-full p-0.5 hover:bg-foreground/10',
              'focus:outline-none focus:ring-1 focus:ring-ring'
            )}
            aria-label={`Премахни етикет ${label.name}`}
          >
            <X
              className={size === 'sm' ? 'size-3' : 'size-3.5'}
              aria-hidden="true"
            />
          </button>
        )}
      </span>
    );
  }
);

LabelBadge.displayName = 'LabelBadge';
